import useIsOutOfTop from '@flight-workspace/common-client/src/hooks/useIsOutOfTop';
import {
  BannerLocation,
  BannerPosition,
  RollingBanner,
} from '@techminds/ad-banner';
import { PageType } from '@travel-ui/my-widget';
import classNames from 'classnames/bind';
import {
  carbonEmissionFareFilter,
  getHasCarbonEmissionAverageInfo,
} from 'common/emissions';
import Footer from 'common/footer';
import GNB from 'common/header';
import ModalContainer from 'common/modal';
import MyWidget from 'common/myWidget';
import PriceGraphDetail from 'common/priceGraph/detail';
import PriceGraph from 'common/priceGraph/list';
import { Position } from 'common/searchBox';
import SideBanner from 'common/sideBanner';
import { DateFormat } from 'common/types';
import UnsupportedBrowser from 'common/unsupportedBrowser';
import { days, getDepartureArrivalHighlight, getTripInfo } from 'common/utils';
import config from 'config/index';
import { CurationContext } from 'contexts/curation';
import { InfoParamsContextProvider } from 'contexts/infoParams';
import { InternationalContext } from 'contexts/international';
import {
  FilterKeys,
  InternationalFilterContext,
} from 'contexts/internationalFilter';
import {
  IModal,
  Modal,
  ModalFuncContext,
  ModalPosition,
} from 'contexts/modalFunc';
import { usePriceTransparency } from 'contexts/priceTransparency';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { SearchType, TripType } from 'contexts/searchParams';
import DetailSchedule from 'detail/detailSchedule';
import Detail from 'detail/index';
import useAirportDetailList, {
  IAirportDetail,
  getSummarizedLocationCodes,
} from 'hooks/useAirportDetailList';
import useCuration from 'hooks/useCuration';
import { Trip, useAddHistory } from 'hooks/useHistory';
import useInternationalFilter from 'hooks/useInternationalFilter';
import useInternationalFlight from 'hooks/useInternationalFlight';
import useInternationalPromotions from 'hooks/useInternationalPromotions';
import useLoadingProgress from 'hooks/useLoadingProgress';
import useScroll, { internationalListComponentId } from 'hooks/useScroll';
import SearchCondition from 'list/common/searchCondition';
import LineBanner from 'main/common/lineBanner';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import selectTypeStorage from 'storage/internationalSelectType';
import InlineFilter from './filter/inlineFilter';
import InternationalFilter from './filter/internationalFilter';
import ListHeader from './header';
import styles from './index.module.scss';
import InternationalListContainer from './listContainer';
import NoDirect from './noDirect';
import NoSelectedResult from './noSelectedResult';
import PopularFlight from './popularFlight';
import {
  IQueryValue,
  Popovers,
  SelectTypeKeys,
  SortKeys,
  SortOrders,
} from './types';
import {
  getWatchingQueryValues,
  parseSelectedFlight,
  routeChange,
  setRoute,
} from './utils';

const cx = classNames.bind(styles);

const adUnitIds = [
  'tm_travel_flight_searchlist_rolling_image_banner_0',
  'tm_travel_flight_searchlist_rolling_image_banner_1',
  'tm_travel_flight_searchlist_rolling_image_banner_2',
  'tm_travel_flight_searchlist_rolling_image_banner_3',
  'tm_travel_flight_searchlist_rolling_image_banner_4',
  'tm_travel_flight_searchlist_rolling_image_banner_5',
  'tm_travel_flight_searchlist_rolling_image_banner_6',
  'tm_travel_flight_searchlist_rolling_image_banner_7',
  'tm_travel_flight_searchlist_rolling_image_banner_8',
  'tm_travel_flight_searchlist_rolling_image_banner_9',
].slice(0, config.maxRollingBannerCount);

const ScheduleTitles = {
  [TripType.OW]: () => ['편도'],
  [TripType.RT]: () => ['가는 편', '오는 편'],
  [TripType.MD]: (iti: any[]) => iti.map((_, i) => `${i + 1}여정 항공편`),
  [TripType.EVERYWHERE]: () => [],
  [TripType.NULL]: () => [],
};

const InternationalContainer = ({
  queryVariables,
  samePriceOrgSch,
  itineraryIataDetails,
}: {
  queryVariables: IQueryValue;
  samePriceOrgSch?: string;
  itineraryIataDetails: IAirportDetail[];
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { setSelectTypeToStorage } = selectTypeStorage();
  const {
    itinerary,
    selectedFlight,
    fareType,
    adult,
    child,
    infant,
    isDirect,
    stayLength,
  } = queryVariables;
  const orgTripType = getTripInfo(itinerary);
  // 도착일 미정은 왕복 조회로 구분되지만 목록은 편도의 UI와 동일
  const tripTypeOfListView = stayLength ? TripType.OW : orgTripType;
  const { orgFlights, sortBy, selectType, setSortBy, expFlights } =
    useContext(InternationalContext);
  const airportCodes = getSummarizedLocationCodes(itinerary);
  const { data: { airportDetailList } = { airportDetailList: [] } } =
    useAirportDetailList(airportCodes);
  const flightResult = useInternationalFlight(
    queryVariables,
    itineraryIataDetails,
  );

  const [minFare, setMinFare] = useState<number>();
  const [cardFareType, setCardFareType] = useState<string>();
  const { priceTransparency, setAirports, setSelectedScheduleDetails } =
    usePriceTransparency();
  const promotions = useInternationalPromotions(fareType, orgTripType);
  const [selectedLabels, setSelectedLabels] = useState<string[][]>([]);
  const hasNoData = !flightResult || !airportDetailList;

  if (hasNoData) return null;

  const { completed, error, fares, resCnt, totalResCnt, schedules } =
    flightResult;
  const [modal, setModal] = useState<IModal>({
    type: Modal.NULL,
    position: ModalPosition.LIST,
  });
  const [popover, setPopover] = useState<Popovers>(Popovers.none);
  const [directToDetail, setDirectToDetail] = useState<boolean>(false);
  const [hasNoResult, setHasNoResult] = useState(false);
  const {
    filterModal,
    filterState,
    filterStore,
    setCurItinSeq,
    setFilterState,
  } = useContext(InternationalFilterContext);
  const { applyFilter, resetFilter } = useInternationalFilter(itinerary);
  const { scrollY, moveScroll } = useScroll(internationalListComponentId);
  const { startReloadTimer } = useReloadTimerContext();
  const selectedFlightList = selectedFlight
    ? parseSelectedFlight(selectedFlight)
    : [];
  // 도착일 미정은 왕복 여정이지만 편도 선택과 동일한 방식
  const allSelected = stayLength
    ? selectedFlightList.length === 1
    : selectedFlightList.length === itinerary?.length;
  const scheduleTitles = ScheduleTitles[orgTripType](itinerary);
  let allDetailLoaded = true;
  const selectedScheduleDetails = selectedFlightList.map(
    (selectedSchedule, index) =>
      schedules?.[index]?.[selectedSchedule?.[0]]?.detail,
  );
  const scheduleDetails = selectedFlightList.map(([sch], i) => {
    const { detail, journeyTime } = schedules?.[i]?.[sch] || {
      detail: [],
      journeyTime: [],
    };
    if (!detail.length) allDetailLoaded = false;
    const { departureHighlight, arrivalHighlight } =
      getDepartureArrivalHighlight({
        schedule: selectedScheduleDetails,
        index: i,
        isAllSelected: allSelected,
      });
    return {
      schedule: detail,
      title: scheduleTitles[i],
      journeyTime,
      departureHighlight,
      arrivalHighlight,
    };
  });
  const {
    initiate: initiateProgress,
    progress,
    isOnLoading,
  } = useLoadingProgress({
    resCnt,
    totalResCnt,
    duration: 10000,
    moreFlag: !completed,
  });
  const curation = useCuration(itinerary, flightResult, !isOnLoading);

  const togglePopover = (popoverKey: Popovers) => {
    setPopover(popover === popoverKey ? Popovers.none : popoverKey);
  };
  const closePopover = () => togglePopover(Popovers.none);
  const onSortByClick = (key: SortKeys, targetIndex: number) => {
    const { key: prevKey, order: prevOrder } = sortBy;
    const order =
      key === prevKey
        ? prevOrder === SortOrders.ascending
          ? SortOrders.descending
          : SortOrders.ascending
        : SortOrders.ascending;
    setSortBy({ key, order, targetIndex });
    closePopover();
  };

  const setSelectTypeAndClose = (s: SelectTypeKeys) => {
    routeChange({ router, method: 'push', selectedFlight: '', selectType: s });
    setSelectTypeToStorage(s);
    closePopover();
  };

  const clearSelectedSchs = (index: number) => {
    setSortBy(prev => ({ ...prev, targetIndex: index }));
    setRoute({
      router,
      method: 'replace',
      flights: selectedFlightList.slice(0, index),
      selectType,
      callback: () => setSelectedLabels(prev => prev.slice(0, index)),
    });
  };

  // 편도 또는 다구간에서 하나의 여정 선택
  const selectFlight = (
    index: number,
    sch: string,
    _fareType: string,
    airline: string,
    labels: string[],
  ) => {
    moveScroll({
      target: scrollRef.current,
      rememberPrevScrollTop:
        tripTypeOfListView === TripType.OW ||
        // 다구간에서 2여정 이후 선택 중인 경우에만 최상단으로 이동하고, 나머지는 스크롤 저장
        // https://oss.navercorp.com/techminds/bts/issues/3747#issuecomment-15336890
        (tripTypeOfListView === TripType.MD && selectedLabels.length === 0),
    });
    setSortBy(prev => ({ ...prev, targetIndex: index + 1 }));
    setRoute({
      router,
      method: 'push',
      flights: selectedFlightList.toSpliced(index, 1, [
        sch,
        _fareType,
        airline,
      ]),
      selectType,
    });
    setSelectedLabels(prev => prev.toSpliced(index, 1, labels));
  };

  // 왕복
  const selectFlights = (
    schs: string,
    _fareType: string,
    airline: string,
    labels: string[],
  ) => {
    const flights: [string, string, string][] = schs
      .split('|')
      .map(sch => [sch, _fareType, airline]);
    moveScroll({
      target: scrollRef.current,
      rememberPrevScrollTop: true,
    });
    setRoute({ router, method: 'push', flights, selectType: 'concurrent' });
    setSelectedLabels([labels]);
  };

  const doModal = (modalInfo: IModal) => {
    if (modalInfo.type === Modal.SIMILAR) {
      setModal({
        ...modalInfo,
        payload: {
          flightResult,
          promotions,
          samePriceOrgSch: modalInfo.payload?.samePriceOrgSch,
          selectFlights,
        },
      });
    } else {
      setModal(modalInfo);
    }
  };

  useAddHistory({
    trip: orgTripType,
    fareType:
      Array.isArray(fareType) && fareType.length
        ? fareType[0]
        : (fareType as string),
    searchType: SearchType.INTERNATIONAL,
    isDirect: isDirect || false,
    passenger: { adult, child, infant },
    tripList: itinerary.map(item => new Trip(item)),
  });

  useEffect(() => {
    setHasNoResult(false);
    initiateProgress();
    if (allSelected) {
      setDirectToDetail(true);
    } else {
      setDirectToDetail(false);
    }
  }, [getWatchingQueryValues(queryVariables)]);

  useEffect(closePopover, [filterModal, modal.type]);

  useEffect(() => {
    // 여정별 선택시 필터 세팅을 위해 현재 선택중인 여정순서 전달
    setCurItinSeq(selectedFlightList.length);

    // targetIndex는 스케쥴 추가/제거 클릭 시점에 핸들러 함수 내에서 변경됨
    // 따라서 뒤로가기/앞으로가기 등 브라우저 기능으로 화면 이동 시, targetIndex와 화면 sync가 안맞는 경우 발생
    // 위와 같은 경우를 대비한 동기화 코드
    const selectedFlightLength = (selectedFlight?.split?.(',') || []).length;
    if (sortBy?.targetIndex !== selectedFlightLength) {
      setSortBy({ ...sortBy, targetIndex: selectedFlightLength });
    }
  }, [selectedFlight]);

  useEffect(() => {
    if (!isOnLoading) {
      startReloadTimer();
      if (!allDetailLoaded && fares?.size) {
        // 로딩완료시에 해당 정보가 없는 경우 - 선택정보 초기화하고 목록뷰로 전환.
        setHasNoResult(true);
        clearSelectedSchs(0);
        moveScroll({});
      }
    }
  }, [isOnLoading]);

  useEffect(() => {
    setAirports(flightResult?.airports ?? {});
    setSelectedScheduleDetails(
      selectedScheduleDetails.flatMap(
        selectedScheduleDetail => selectedScheduleDetail ?? [],
      ) ?? [],
    );
  }, [priceTransparency]);

  const rerunCarbonEmissionOnlyFilter = () => {
    const hasCarbonEmissionAverageInfo =
      getHasCarbonEmissionAverageInfo(orgFlights);
    if (
      Array.from(Object.keys(orgFlights?.results.fares || {})).filter(
        carbonEmissionFareFilter(
          hasCarbonEmissionAverageInfo,
          orgFlights,
          selectType,
        ),
      ).length
    ) {
      applyFilter(filterStore);
    } else {
      setFilterState(FilterKeys.carbonEmissionOnly, false);
      filterStore.carbonEmissionOnlyFilter = false;
      applyFilter(filterStore);
    }
  };

  const noResult = !(
    orgFlights?.results.fares &&
    Object.values(orgFlights.results.fares).length > 0
  );

  useEffect(() => {
    if (!isOnLoading && !noResult) {
      rerunCarbonEmissionOnlyFilter();
    }
  }, [selectType]);

  const excludeCard = filterState[FilterKeys.excludeCard];

  function onExcludeCardFilterClick(excludeCardFilter: boolean) {
    setFilterState(FilterKeys.excludeCard, excludeCardFilter);
    filterStore.excludeCardFilter = excludeCardFilter;
    applyFilter(filterStore);
    closePopover();
  }

  const showLoading = (isOnLoading && !fares?.size) || !allDetailLoaded;

  const [topRef, isOutOfTop] = useIsOutOfTop(hasNoData || showLoading);

  useEffect(() => {
    if (!document.scrollingElement || router.query.selectedFlight) {
      return;
    }
    if (
      !(tripTypeOfListView === TripType.MD && selectedFlightList.length > 0)
    ) {
      document.scrollingElement.scrollTop = scrollY.current;
    }
  }, [allSelected, tripTypeOfListView, selectedFlightList.length]);

  return (
    <ModalFuncContext.Provider value={doModal}>
      <InfoParamsContextProvider>
        <div
          className={`flights List ${cx('InternationalContainer')}`}
          data-container={allSelected ? 'detail' : 'list'}
          data-fixed={isOutOfTop}
        >
          <main id="container">
            <UnsupportedBrowser />
            <GNB />
            <div className={cx('top')} ref={topRef}>
              <div className={cx('inner')}>
                <div className={cx('search_condition_wrapper')}>
                  <SearchCondition
                    adult={adult}
                    child={child}
                    infant={infant}
                    disablePriceAlert={noResult}
                    expandMultiCity={allSelected}
                    fareType={fareType}
                    isDirect={isDirect}
                    isLoading={isOnLoading}
                    itinerary={itinerary}
                    position={Position.LIST}
                    stayLength={stayLength}
                    tripType={orgTripType}
                  />
                  <div
                    className={cx(
                      'search_condition_loading_area',
                      !isOnLoading && 'loading_done',
                    )}
                    aria-hidden={!isOnLoading}
                    aria-label="조건에 맞는 항공편 검색중"
                    aria-busy="true"
                  >
                    <div className={cx('inner')}>
                      <div className={cx('loading_bar')} />
                    </div>
                  </div>
                </div>

                {!allSelected && (
                  <InlineFilter
                    itinerary={itinerary}
                    isOnLoading={isOnLoading}
                  />
                )}
              </div>
            </div>
            <RollingBanner
              adUnitIds={adUnitIds}
              position={BannerPosition.LIST}
              location={BannerLocation.OVERSEAS}
            />
            <div className={cx('my_widget_container')}>
              <div className={cx('inner')}>
                <MyWidget
                  pageType={PageType.LIST}
                  nClickCodePrefix={allSelected ? 'idt.' : 'isr.'}
                />
              </div>
            </div>
            {!allSelected && tripTypeOfListView !== TripType.MD && (
              <CurationContext.Provider value={curation}>
                <PopularFlight
                  flightResult={flightResult}
                  isOnLoading={isOnLoading}
                  promotions={promotions}
                  queryVariables={queryVariables}
                  selectedFlightList={selectedFlightList}
                  tripType={tripTypeOfListView}
                  selectFlight={selectFlight}
                  selectFlights={selectFlights}
                />
              </CurationContext.Provider>
            )}
            {!isOnLoading && !allSelected && !noResult && minFare && (
              <PriceGraph
                fareType={fareType}
                isDirect={!!isDirect}
                itinerary={itinerary}
                minFare={minFare}
              />
            )}
            <div id={internationalListComponentId} className={cx('content')}>
              <div className={cx('inner')} ref={scrollRef}>
                {!isOnLoading && (
                  <>
                    {isDirect && !fares.size && (
                      <NoDirect
                        adult={adult}
                        child={child}
                        infant={infant}
                        fareType={fareType}
                        isDirect={isDirect}
                        itinerary={itinerary}
                      />
                    )}
                    {hasNoResult && <NoSelectedResult />}
                  </>
                )}
                {!allSelected && (
                  <ListHeader
                    excludeCard={excludeCard}
                    popover={popover}
                    samePriceOrgSch={samePriceOrgSch}
                    selectType={selectType}
                    stayLength={stayLength}
                    targetIndex={selectedFlightList.length}
                    tripType={tripTypeOfListView}
                    goBack={router.back}
                    onExcludeCardFilterClick={onExcludeCardFilterClick}
                    onSortByClick={onSortByClick}
                    setSelectTypeAndClose={setSelectTypeAndClose}
                    togglePopover={togglePopover}
                  />
                )}
                <div className={cx('selected_flights')}>
                  {!!scheduleDetails.length &&
                    scheduleDetails.map(
                      (
                        {
                          schedule,
                          title,
                          journeyTime,
                          departureHighlight,
                          arrivalHighlight,
                        },
                        i,
                      ) => (
                        <DetailSchedule
                          key={`flight#_${i}`}
                          arrivalHighlight={arrivalHighlight}
                          block={itinerary.length !== 2}
                          departureHighlight={departureHighlight}
                          flightResult={flightResult}
                          isLoading={!allDetailLoaded}
                          journeyTime={journeyTime}
                          router={router}
                          schedule={schedule}
                          scheduleIndex={i}
                          searchType={SearchType.INTERNATIONAL}
                          selectType={selectType}
                          title={title}
                          removeSchedule={() => clearSelectedSchs(i)}
                        />
                      ),
                    )}
                  {itinerary.length === 2 &&
                    scheduleDetails.length === 1 &&
                    !stayLength && (
                      <div className={cx('unset')}>
                        <div className={cx('inner')}>
                          <div className={cx('heading')}>
                            <b className={cx('name')}>{scheduleTitles[1]}</b>
                            <i className={cx('date')}>
                              {days(
                                itinerary[1].departureDate,
                                DateFormat.YMD,
                              ).format(DateFormat.DotMDd)}
                            </i>
                          </div>
                          <div className={cx('desc')}>
                            {scheduleTitles[1]}을 선택하세요
                          </div>
                        </div>
                      </div>
                    )}
                </div>
                {!isOnLoading && allSelected && allDetailLoaded && minFare && (
                  <PriceGraphDetail
                    fareType={fareType}
                    includeEventFare={cardFareType !== 'A01'}
                    minFare={minFare}
                    selectedFlightList={selectedFlightList}
                  />
                )}
                <CurationContext.Provider value={curation}>
                  <InternationalListContainer
                    allSelected={allSelected}
                    error={error}
                    fareType={fareType}
                    flightResult={flightResult}
                    itinerary={itinerary}
                    noResult={noResult}
                    promotions={promotions}
                    samePriceOrgSch={samePriceOrgSch}
                    scheduleTitles={scheduleTitles}
                    selectedFlightList={selectedFlightList}
                    selectType={selectType}
                    tripType={tripTypeOfListView}
                    onReset={resetFilter}
                    selectFlight={selectFlight}
                    selectFlights={selectFlights}
                    setMinFare={setMinFare}
                    showLoading={showLoading}
                    allDetailLoaded={allDetailLoaded}
                  />
                </CurationContext.Provider>
                {(!error || isOnLoading) && allSelected && (
                  <Detail
                    loaded={!isOnLoading}
                    promotions={promotions}
                    queryVariables={queryVariables}
                    selectedLabels={selectedLabels}
                    setCardFareType={setCardFareType}
                    setMinFare={setMinFare}
                  />
                )}
              </div>
            </div>
          </main>
          <Footer />
          {filterModal && (
            <InternationalFilter
              itinerary={itinerary}
              tripType={tripTypeOfListView}
            />
          )}
          {/* 시크릿쿠폰 토글 팝업 view 샘플 */}
          <LineBanner />
          <SideBanner />
          <ModalContainer
            modal={modal}
            setModal={doModal}
            isSearchBoxFixed={isOutOfTop}
          />
        </div>
      </InfoParamsContextProvider>
    </ModalFuncContext.Provider>
  );
};

export default InternationalContainer;
